<template>
    <div class="d flex flex-column">
        <v-row class="ma-0">
            <v-col class="px-0 d-flex justify-end" cols="12">
                <v-btn 
                    color="primary" 
                    prepend-icon="mdi-plus" 
                    class="mt-4 mb-2 text-none" 
                    :diabled="canCreateTrackingGroups" 
                    @click="
                        canCreateTrackingGroups
                        ? openDialogForAction('create')
                        : null    
                    "
                >Criar Grupo</v-btn>
            </v-col>
        </v-row>

        <v-data-table-server
            v-model:items-per-page="filters.per_page"
            v-model:page="filters.page"
            :mobile="mdAndDown"
            hide-default-footer
            @update:sort-by="(newOrder) => handleSortingUpdate(newOrder)"
            :items-length="filters?.total ?? 0"
            :loading="isFetchingGroupsList"
            :loading-text="'Obtendo lista de Grupos...'"
            :headers="trackingGroupsTableHeaders"
            :items="trackingGroupsList"
        >
            <template #item.actions="{item}">
                <div class="d-flex flex-column flex-md-row justify-lg-end ga-4 align-center">
                    <v-tooltip
                        v-if="item?.can_update"
                        text="Editar Grupo" 
                        location="top"
                    >
                        <template #activator="{props}">
                            <v-btn 
                                v-bind="props" 
                                icon="mdi-pencil" 
                                color="primary" 
                                rounded="xl" 
                                size="x-small" 
                                :disabled="isFetchingGroupsList" 
                                @click="openDialogForAction('update', item)"></v-btn>
                        </template>
                    </v-tooltip>
                    <v-tooltip 
                        v-if="item?.can_get" 
                        text="Ver Detalhes" 
                        location="top"
                    >
                        <template #activator="{props}">
                            <v-btn 
                                v-bind="props" 
                                icon="mdi-eye" 
                                color="info" 
                                rounded="xl" 
                                size="x-small" 
                                :disabled="isFetchingGroupsList" 
                                @click="openDialogForAction('get', item)"
                            ></v-btn>
                        </template>
                    </v-tooltip>
                    <v-tooltip
                        v-if="item?.can_delete" 
                        text="Apagar Grupo" 
                        location="top"
                    >
                        <template #activator="{props}">
                            <v-btn 
                                v-bind="props" 
                                icon="mdi-trash-can" 
                                color="error" 
                                rounded="xl" 
                                size="x-small" 
                                :disabled="isFetchingGroupsList" 
                                @click="openDialogForAction('delete', item)"
                            ></v-btn>
                        </template>
                    </v-tooltip>
                </div>
            </template>
        </v-data-table-server>

        <div class="d-flex justify-end align-center">
            <strong v-if="!first_load">
                {{ filters.from ?? 1 }}
                -
                {{ filters.to ?? filters.per_page ?? 10 }}
                /
                {{ filters.total ?? 0 }}
            </strong>

            <v-pagination
                v-model="filters.page"
                :length="filters.last_page ?? 1"
                :total-visible="0"
                @update:model-value="fetchTrackingGroups"
            />
        </div>
    </div>
</template>
<script setup>
import { useDisplay } from 'vuetify';
import { useSessionStore } from '../../../../../stores/SessionStore';
import { computed, onMounted, readonly, ref, resolveComponent } from 'vue';
import DmrTrackingGroups from '../../../../../api/departments/membership-requests/tracking-groups/DmrTrackingGroups';
import { useDialog } from '@internal_projects_dd/universal-dialog';
import manipulators from "@/composables/stringManipulations.js"
import actionReferences from '../../../../../composables/actionReferences';
import DMRTrackingGroupsForm from './DMRTrackingGroupsForm.vue';

const { mdAndDown } = useDisplay();
const sessionStore = useSessionStore();

const dialog = useDialog();

const trackingGroupsTableHeaders = [
    {
        title: "Grupo",
        key: "name",
        sortable: true,
        align: "center",
    },
    {
        title: "Directores Grupo",
        key: "managers_count",
        sortable: true,
        align: "end",
    },
    {
        title: "Membros",
        key: "tracked_count",
        sortable: true,
        align: "end",
    },
    {
        title: "Ações",
        key: "actions",
        sortable: false,
        align: "end"
    }
];
const first_load = ref(false);
const trackingGroupsList = ref([]);
const isFetchingGroupsList = ref(false);
const isFetchingManagersList = ref(false);
const eligibleManagersList = ref([]);
const isFetchingUsersList = ref(false);
const usersList = ref([]);

const filters = ref({
    page: 1,
    per_page: 10,
    order_by: "name",
    direction: "asc",
    manager_user_ids: null,
    tracked_user_ids: null,
    from: 1,
    to: 1,
    total: 1,
})

const canCreateTrackingGroups = computed(() => {
    return sessionStore.hasPermission([
      "user.department.membership.request.tracking-group.create",
      "user.department.membership.request.tracking-group.manage.any",
    ])
});

async function openDialogForAction(action = "create", group = null) {

    const idempotency_key = self?.crypto?.randomUUID() ?? null;

    const componentProperties = 
        resolveComponentPropertiesForAction(
            action,
            group
        );

    componentProperties.idempotency_key = idempotency_key;
    
    // componentProperties.onSubmit = createSubmitHandlerForAction(action, group);

    const actionSpecificDialogOptions = 
        resolveDialogOptionsForAction(
            action
        );

    await dialog.open({
        component: DMRTrackingGroupsForm,

        props : {
            ...componentProperties,
        },

        options: {
            ...actionSpecificDialogOptions,
        },
    });

}

function resolveDialogOptionsForAction(action) {
    actionReferences.throwOnIllegalCrudAction(action);

    const dialogOptions = {
        titleBackgroundColor: "transparent",
        titleTextColor: "primary",
        persistent: true,
        maxWidth: "500",
        showCancel: false,
        onConfirm: "submit",
        confirmActionColor: "primary",
        onConfirmFailureExpectedResult: false,
        onConfirmFailureThrow: true,
    };

    let uiActionVerb;
    let uiActionAdjective;
    let uiActionUnderWayLabel;
    let apiMethod;

    switch (true) {
        case actionReferences.isValidCreateAction(action):
            
            uiActionVerb = "criar";
            uiActionAdjective = "criado";
            uiActionUnderWayLabel = "criando";

            dialogOptions.confirmActionIcon = "mdi-plus";

            apiMethod = "create";
            
            break;

        case actionReferences.isValidUpdateAction(action):

            uiActionVerb = "atualizar";
            uiActionAdjective = "atualizado";
            uiActionUnderWayLabel = "atualizando";

            dialogOptions.confirmActionIcon = "mdi-content-save";

            apiMethod = "update";
            
            break;

        case actionReferences.isValidDeleteAction(action):

            uiActionVerb = "apagar";
            uiActionAdjective = "apagado";
            uiActionUnderWayLabel = "apagando";

            dialogOptions.confirmActionIcon = "mdi-delete";
            dialogOptions.confirmActionColor = "error";

            apiMethod = "delete";

            break;

        
        default:
            break;
    }

    if(actionReferences.isValidGetAction(action)) {
        dialogOptions.onConfirm = null;
            dialogOptions.confirmActionIcon = "mdi-close";
            dialogOptions.persistent = false;
            dialogOptions.title = "Detalhes do Grupo";
            dialogOptions.confirmActionText = "Fechar Detalhes"
            dialogOptions.onConfirmActionText = "Fechando Detalhes";

            apiMethod = null;

            dialogOptions.onConfirm = async (detailsDialog) => {
                return true;
            }

        return dialogOptions;
    }

    dialogOptions.title = `${manipulators.firstLetterToUpperCase(uiActionVerb)} Grupo`;
    dialogOptions.confirmActionText = `${manipulators.firstLetterToUpperCase(uiActionVerb)} Grupo`;
    dialogOptions.onConfirmActionText = `${manipulators.firstLetterToUpperCase(uiActionUnderWayLabel)} Grupo...`;
    
    dialogOptions.onConfirmSuccess = async (result) => {
        fetchTrackingGroups();

        await openSuccessDialog(
            `Grupo ${uiActionAdjective} com sucesso!`
        );

        return result;
    }

    dialogOptions.onConfirmError = async (error, currentDialog) => {
        console.error(
            `Erro a ${uiActionVerb} Grupo:`,
            String(error)
        );

        currentDialog.options.onConfirmFailedErrorTitle = 
                    error?.reason
                    ?? `Erro a ${uiActionVerb} Grupo:`;

                currentDialog.options.onConfirmFailedErrorMessage = 
                    (Object.values(error?.response?.data?.errors ?? {})?.flat().join("\n")
                    || error?.response?.data?.message)
                    ?? error?.message
                    ?? String(error)
                    ?? `Não foi possível ${uiActionVerb} o Grupo! Tente novamente`;

        return false;
    }


    return dialogOptions;
}

async function openSuccessDialog(bodyText) {
    await dialog.open({
        options: {
            bodyText,
            persistent: true,
            maxWidth: 500,
            showCancel: false,
            confirmActionIcon: "",
            confirmActionText: "Ok",
            confirmActionColor: "primary",
        },
    });
}

function resolveComponentPropertiesForAction(action = "create", group = null) {

    actionReferences.throwOnIllegalCrudAction(action);
    
    const properties = {
        standalone: false,
        action: action,
        usersList: usersList.value,
        isFetchingUsersList: isFetchingUsersList.value,
        managersList: eligibleManagersList.value,
        isFetchingManagersList: isFetchingManagersList.value,
        readonly: actionReferences.isValidReadonlyAction(action),
    };
    
    if(group === null || group === undefined) {
        properties.action = "create";
    }
    else {
        
        if(typeof group === "string") {

            properties.trackingGroupId = group

        } else if(typeof group === "object" && group?.id) {
            
            properties.trackingGroupObject = {...group};

        } else {
            throw new ReferenceError("Need to provide a value for group.")
        }
    }  
    
    return properties;
}

async function fetchTrackingGroups() {
    if(isFetchingGroupsList.value) {
        return;
    }

    try {
        isFetchingGroupsList.value = true;

        const groupsResponse = await DmrTrackingGroups.all(filters.value);

        trackingGroupsList.value = groupsResponse?.data?.data
                                ?? groupsResponse?.data
                                ?? [];

        filters.value = {
            ...filters.value,
            page: groupsResponse?.data?.cur_page 
                    ?? 1,
            per_page: groupsResponse?.data?.per_page 
                        ?? 10,
            from: groupsResponse?.data?.from 
                    ?? 1,
            to: groupsResponse?.data?.to 
                ?? 1,
            total: groupsResponse?.data?.total 
                    ?? trackingGroupsList.value?.length,
            
        }
        
    } catch (error) {
        
    } finally {
        isFetchingGroupsList.value = false;

        first_load.value = false;
    }
}

onMounted(() => {
    first_load.value = true;
    fetchTrackingGroups();
})

</script>